import GapFlex from 'components/GapFlex'
import {
  ChevronDown as ChevronDownIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ChevronUp as ChevronUpIcon,
} from 'components/Icons'
import Loader from 'components/Loader'
import PropTypes from 'prop-types'
import React, { useContext, useEffect } from 'react'
import { usePagination, useSortBy, useTable } from 'react-table'
import { Flex } from 'reflexbox/styled-components'
import { ThemeContext } from 'styled-components'
import GoToPageInput from './GoToPageInput'
import PageSizeSelect from './PageSizeSelect'
import {
  CardContainer,
  NavigationButton,
  NavigationInfo,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHeader,
  TableRow,
} from './styles'

const DataListingTable = ({ columns, data, loading, fetchData, pagination }) => {
  const theme = useContext(ThemeContext)
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      columns,
      data,
      manualSortBy: true,
      manualPagination: true,
      pageCount: pagination.totalPages || 1,
      initialState: {
        pageSize: pagination.pageSize,
        pageIndex: pagination.currentPage - 1,
      },
    },
    useSortBy,
    usePagination,
  )

  useEffect(() => {
    gotoPage(pagination.currentPage - 1)
  }, [pagination.currentPage])

  useEffect(() => {
    fetchData({ sortBy, currentPage: pageIndex + 1, pageSize })
  }, [sortBy, pageIndex, pageSize])

  return (
    <Flex flex='1' flexDirection='column'>
      <CardContainer>
        <TableContainer {...getTableProps()}>
          <TableHeader>
            {headerGroups.map(headerGroup => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <TableCell
                    customStyle={column.customStyle}
                    as='th'
                    {...column.getHeaderProps(
                      column.getSortByToggleProps(
                        column.canSort && { title: `Ordenar por ${column.render('Header')}` },
                      ),
                    )}
                  >
                    {column.render('Header')}
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <ChevronDownIcon color={theme.colors.grays.N600} />
                      ) : (
                        <ChevronUpIcon color={theme.colors.grays.N600} />
                      )
                    ) : null}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody {...getTableBodyProps()}>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align='center'>
                  <Loader />
                </TableCell>
              </TableRow>
            ) : (
              rows.map(row => {
                prepareRow(row)
                return (
                  <TableRow {...row.getRowProps()}>
                    {row.cells.map(cell => {
                      return <TableCell {...cell.getCellProps()}>{cell.render('Cell')}</TableCell>
                    })}
                  </TableRow>
                )
              })
            )}
          </TableBody>
          {!loading && (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={columns.length} align={pagination.totalRecords ? 'right' : 'center'}>
                  {pagination.totalRecords
                    ? `Mostrando ${page.length} de ${pagination.totalRecords} registros`
                    : 'Nenhum registro encontrado'}
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </TableContainer>
      </CardContainer>
      <Flex flexWrap='wrap' alignItems='center' mt={4}>
        <Flex
          flexBasis={['60%', '50%', '40%', '60%']}
          flexGrow='1'
          alignItems='center'
          minWidth='290px'
          pr={[0, 0, 3]}
          mb={3}
          justifyContent={['center', 'center', 'center', 'flex-end']}
        >
          <NavigationButton
            onClick={event => {
              if (!event.detail || event.detail == 1) {
                gotoPage(0)
              }
            }}
            disabled={!canPreviousPage}
          >
            <ChevronLeftIcon double />
          </NavigationButton>

          <NavigationButton
            onClick={event => {
              if (!event.detail || event.detail == 1) {
                previousPage()
              }
            }}
            disabled={!canPreviousPage}
          >
            <ChevronLeftIcon />
          </NavigationButton>

          <NavigationInfo>
            Página
            <strong>
              {pageIndex + 1} de {pageOptions.length}
            </strong>
          </NavigationInfo>

          <NavigationButton
            onClick={event => {
              if (!event.detail || event.detail == 1) {
                nextPage()
              }
            }}
            disabled={!canNextPage}
          >
            <ChevronRightIcon />
          </NavigationButton>

          <NavigationButton
            onClick={event => {
              if (!event.detail || event.detail == 1) {
                gotoPage(pageCount - 1)
              }
            }}
            disabled={!canNextPage}
          >
            <ChevronRightIcon double />
          </NavigationButton>
        </Flex>

        <GapFlex
          gap='16px'
          flexBasis={['40%', '50%', '50%', '40%']}
          flexGrow='1'
          minWidth='200px'
          flexWrap='wrap'
          pr={[0, 0, 0, 4]}
        >
          <GoToPageInput defaultValue={pageIndex + 1} onChange={page => gotoPage(page)} />
          <PageSizeSelect
            value={pageSize}
            options={[10, 20, 30, 40, 50].map(pageSize => pageSize)}
            onChange={pageSize => setPageSize(pageSize)}
          />
        </GapFlex>
      </Flex>
    </Flex>
  )
}

DataListingTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
    }),
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  fetchData: PropTypes.func.isRequired,
  pagination: PropTypes.shape({
    totalRecords: PropTypes.number,
    totalPages: PropTypes.number,
    currentPage: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
  }).isRequired,
  loading: PropTypes.bool,
}

DataListingTable.defaultProps = {
  loading: false,
}

export default DataListingTable
